import { useCallback, useEffect } from 'react'

import { useVisitorChannelContext } from '@liveHelper/context/VisitorChannelContext'
import {
  actionsSelector,
  monitoringActionsSelector,
  monitoringSelector,
  useStore,
} from '@liveHelper/models/store'

import { isCurrentDomain } from '@core/helpers/isCurrentDomain'
import { navigateTo } from '@core/helpers/navigateTo'
import {
  getBroadcastChannel,
  useBroadcastChannel,
} from '@core/hooks/useBroadcastChannel'
import { useIsFocusedTab } from '@core/hooks/useIsFocusedTab'

/**
 * Keeps the monitoring state in sync between tabs.
 *
 * This allows us to limit one tab to maintaining a connection to the visitor
 * websocket topic, while giving other tabs a way to react to events received on
 * that first tab.
 */
export const useMonitoringCrossTabCommunication = () => {
  const isMonitoring = useStore(monitoringSelector)
  const isFocusedTab = useIsFocusedTab()
  const { setIsMonitoring } = useStore(actionsSelector)
  const { takeFullSnapshot } = useStore(monitoringActionsSelector)
  const visitorChannel = useVisitorChannelContext()

  // Asks the tab connected to the visitor topic what the value of `isMonitoring`
  // is on initial page load.
  useEffect(() => {
    getBroadcastChannel('monitoring').postMessage({ event: 'monitoring_sync' })
  }, [])

  useBroadcastChannel(
    'monitoring',
    useCallback(
      (ev) => {
        switch (ev.data.event) {
          case 'monitoring_sync': {
            if (visitorChannel && isMonitoring) {
              getBroadcastChannel('monitoring').postMessage({
                event: 'start_monitor',
              })
            }
            break
          }
          case 'start_monitor': {
            if (isMonitoring && isFocusedTab) {
              takeFullSnapshot?.()
            } else {
              setIsMonitoring(true)
            }
            break
          }
          case 'stop_monitor': {
            setIsMonitoring(false)
            break
          }
          case 'navigate': {
            if (isFocusedTab) {
              const { url } = ev.data
              isCurrentDomain(url)
                ? navigateTo(url)
                : window.open(url, '_blank')
            }
            break
          }
          default: {
            break
          }
        }
      },
      [
        visitorChannel,
        isMonitoring,
        isFocusedTab,
        takeFullSnapshot,
        setIsMonitoring,
      ],
    ),
  )
}
